import React, { useState } from 'react';

import Modal from 'components/Modal';
import ButtonGroup from 'components/Button/ButtonGroup';
import Button from 'components/Button';
import { ArtistProfile, DspConnection } from 'types';
import { DIGITAL_SERVICE_PROVIDERS as DSP } from 'types/constants';
import { useUpdateArtistProfileMutation } from 'api';
import { baseCharacterMatch } from './helpers';
import { TextEmphasis } from '@protonradio/proton-ui';

interface NameUpdateModalProps {
  artistProfile: ArtistProfile;
  dspProfile: DspConnection;
  continueToConnection: (dspProfile: DspConnection, artist: ArtistProfile) => void;
  closeModal: () => void;
  createAlert: (alertInput: unknown) => void;
}

export const NameUpdateModal: React.FC<NameUpdateModalProps> = ({
  artistProfile,
  dspProfile,
  continueToConnection,
  closeModal,
  createAlert
}) => {
  const [frozen, setFrozen] = useState(false);
  const [updateArtistProfile, { isLoading }] = useUpdateArtistProfileMutation();
  const isCaseOrAccentOnlyMismatch = baseCharacterMatch(
    artistProfile.name,
    dspProfile.value
  );

  const updateArtistNameThenConnect = async (): Promise<void> => {
    let updatedProfile: ArtistProfile;
    setFrozen(true);
    try {
      updatedProfile = (await updateArtistProfile({
        id: artistProfile.id,
        name: dspProfile.value
      }).unwrap()) as ArtistProfile;
      createAlert({
        message: `Artist's name has been updated to match the ${
          DSP[dspProfile.dsp]
        } profile.`,
        type: 'message',
        timeout: false,
        dismissable: true
      });
      continueToConnection(dspProfile, updatedProfile);
    } catch (error: unknown) {
      createAlert({ error });
    } finally {
      setFrozen(false);
      closeModal();
    }
  };

  const noUpdate = () => {
    closeModal();
    continueToConnection(dspProfile, artistProfile);
  };

  const showIntercomChat = () => {
    closeModal();
    window.Intercom(
      'showNewMessage',
      `I'm trying to connect the ${DSP[dspProfile.dsp]} profile ${dspProfile.value} ` +
        `to the Proton artist ${artistProfile.name}, but I'm getting an error that ` +
        `the name doesn't sufficiently match.\n\n` +
        `Can you help look into this for me? Thank you!`
    );
  };

  const modalMessage = (
    <>
      <Modal.Text>
        {`You’ve selected to connect the ${DSP[dspProfile.dsp]} profile `}
        <strong>{dspProfile.value}</strong> {' to the Proton artist profile '}
        <strong>{artistProfile.name}</strong>
        {'. '}
      </Modal.Text>
      {isCaseOrAccentOnlyMismatch ? (
        <Modal.Text>
          {'Would you like the artist name in Proton to be updated to '}
          <TextEmphasis>{dspProfile.value}</TextEmphasis>
          {'?'}
        </Modal.Text>
      ) : (
        <>
          <Modal.Text>
            {'These names do not exactly match and the artist name in Proton must '}
            {'be updated to ensure music goes to the correct profile.'}
          </Modal.Text>
        </>
      )}
    </>
  );

  return (
    <Modal
      title="Update Artist Name?"
      onClose={closeModal}
      testId={NAME_UPDATE_TEST_IDS.NAME_UPDATE_MODAL}
      frozen={frozen}
    >
      {modalMessage}
      <ButtonGroup justify="right" style={{ marginTop: '3rem' }}>
        {isCaseOrAccentOnlyMismatch ? (
          <Button
            onClick={noUpdate}
            color={Button.COLORS.NEUTRAL}
            disabled={isLoading}
            data-testid={NAME_UPDATE_TEST_IDS.NO_UPDATE_BUTTON}
          >
            {`Don't Update`}
          </Button>
        ) : (
          <Button
            onClick={showIntercomChat}
            color={Button.COLORS.NEUTRAL}
            disabled={isLoading}
            data-testid={NAME_UPDATE_TEST_IDS.INTERCOM_BUTTON}
          >
            {`Contact Support`}
          </Button>
        )}
        <Button
          onClick={updateArtistNameThenConnect}
          disabled={isLoading}
          data-testid={NAME_UPDATE_TEST_IDS.UPDATE_BUTTON}
        >
          {`Sync to ${DSP[dspProfile.dsp]} Name`}
        </Button>
      </ButtonGroup>
    </Modal>
  );
};

export const NAME_UPDATE_TEST_IDS = {
  NAME_UPDATE_MODAL: 'name-update-modal',
  NO_UPDATE_BUTTON: 'no-update-button',
  UPDATE_BUTTON: 'update-button',
  INTERCOM_BUTTON: 'intercom-button'
} as const;
